import type { User } from 'oidc-client-ts';
import { brazeService } from './brazeService';
import type { BrazeUserAttributes } from './types';

class BrazeIdentityService {
  private static instance: BrazeIdentityService;
  private identifiedUserId: string | null = null;
  private pendingIdentification: {
    oidcUser: User;
    subscriptionData?: any;
  } | null = null;
  private initializationCheckInterval: number | null = null;

  private constructor() {}

  public static getInstance(): BrazeIdentityService {
    if (!BrazeIdentityService.instance) {
      BrazeIdentityService.instance = new BrazeIdentityService();
    }
    return BrazeIdentityService.instance;
  }

  public identifyUser(oidcUser: User, subscriptionData?: any): void {
    const externalId = oidcUser.profile.sub;
    if (!externalId || this.identifiedUserId === externalId) return;

    if (!brazeService.isInitialized()) {
      this.pendingIdentification = { oidcUser, subscriptionData };
      this.startInitializationCheck();
      return;
    }

    this.performIdentification(oidcUser, subscriptionData);
  }

  private performIdentification(oidcUser: User, subscriptionData?: any): void {
    const externalId = oidcUser.profile.sub;
    if (!externalId) return;

    try {
      if (brazeService.isAnonymousUser()) {
        localStorage.removeItem('braze_anonymous_user_id');
      }

      brazeService.identifyUser(externalId);
      this.identifiedUserId = externalId;

      const attributes = this.buildUserAttributes(oidcUser, subscriptionData);
      brazeService.setUserAttributes(attributes);
    } catch (error) {
      console.error('[Braze Identity] Failed to identify user:', error);
    }
  }

  private startInitializationCheck(): void {
    if (this.initializationCheckInterval) {
      clearInterval(this.initializationCheckInterval);
    }

    let attempts = 0;
    const maxAttempts = 100;

    this.initializationCheckInterval = window.setInterval(() => {
      attempts++;

      if (brazeService.isInitialized() && this.pendingIdentification) {
        const { oidcUser, subscriptionData } = this.pendingIdentification;
        this.clearInitializationCheck();
        this.performIdentification(oidcUser, subscriptionData);
      } else if (attempts >= maxAttempts) {
        this.clearInitializationCheck();
      }
    }, 100);
  }

  private clearInitializationCheck(): void {
    this.pendingIdentification = null;
    if (this.initializationCheckInterval) {
      clearInterval(this.initializationCheckInterval);
      this.initializationCheckInterval = null;
    }
  }

  private buildUserAttributes(
    oidcUser: User,
    subscriptionData?: any,
  ): BrazeUserAttributes {
    const { profile } = oidcUser;
    const attributes: BrazeUserAttributes = {};

    attributes.product_ids = ['napster_app'];

    if (profile.email) attributes.email = profile.email as string;
    if (profile.firstName) attributes.first_name = profile.firstName as string;
    if (profile.lastName) attributes.last_name = profile.lastName as string;
    if (subscriptionData?.plan)
      attributes.subscription_plan = subscriptionData.plan;
    if (subscriptionData?.status)
      attributes.subscription_status = subscriptionData.status;

    if (profile.email) {
      const email = (profile.email as string).toLowerCase();
      attributes.is_employee =
        email.includes('touchcast') ||
        email.includes('napster') ||
        email.includes('theinfinitereality');
    }

    if (profile.iat) {
      attributes.created_at = new Date(
        (profile.iat as number) * 1000,
      ).toISOString();
    }

    return attributes;
  }

  public updateUserAttributes(attributes: BrazeUserAttributes): void {
    if (!brazeService.isInitialized() || !this.identifiedUserId) return;

    try {
      brazeService.setUserAttributes(attributes);
    } catch (error) {
      console.error(
        '[Braze Identity] Failed to update user attributes:',
        error,
      );
    }
  }

  public isUserIdentified(): boolean {
    return this.identifiedUserId !== null;
  }

  public getIdentifiedUserId(): string | null {
    return this.identifiedUserId;
  }
}

export const brazeIdentity = BrazeIdentityService.getInstance();
